import { CopyButton } from "@components/shared/CopyButton";
import {
  REGENERATE_ROLE_BLUEPRINT_DIMENSIONS,
  RELOCK_ROLE_BLUEPRINT,
  START_REGENERATE_RUN,
  TOGGLE_ROLE_BLUEPRINT_APPROVAL,
  TOGGLE_ROLE_BLUEPRINT_LOCK,
  UPDATE_CAPABILITY_DIMENSIONS,
} from "@lib/graphql/mutations";
import { ROLE_BLUEPRINT_QUERY } from "@lib/graphql/queries";
import { useCampaignChannel } from "@lib/hooks/useCampaignChannel";
import { CheckCircle, FileText, Lock, Pencil, Unlock } from "lucide-react";
import { useCallback, useState } from "react";
import { useMutation, useQuery } from "urql";
import { RegenerateConfirmDialog } from "../../shared/RegenerateConfirmDialog";
import { BlueprintAuditPanel } from "../BlueprintAuditPanel";
import { BlueprintStateBadge } from "../BlueprintStateBadge";
import { BlueprintVersionHistory } from "../BlueprintVersionHistory";
import type {
  BlueprintVersion,
  CapabilityDimension,
  LinkedBlueprintArtefact,
  LockEvent,
  LockPrecondition,
  OutOfSyncArtefact,
} from "../blueprintTypes";
import { labelForBriefField } from "../blueprintTypes";
import type { ScreeningCriteria } from "../briefTypes";
import { type ArtefactKind, CascadeGenerationModal } from "../CascadeGenerationModal";
import { LinkedArtefactsPanel } from "../LinkedArtefactsPanel";
import { LockBlueprintModal } from "../LockBlueprintModal";
import { LockPreconditionsList } from "../LockPreconditionsList";
import { RelockBlueprintModal } from "../RelockBlueprintModal";
import { ScreeningCriteriaSection } from "../ScreeningCriteriaSection";
import { StalenessBanner } from "../StalenessBanner";
import { UnlockBlueprintModal } from "../UnlockBlueprintModal";
import { CapabilityDimensionsEditor } from "./CapabilityDimensionsEditor";

const ARTEFACT_KIND_TO_CASCADE_KIND: Record<OutOfSyncArtefact["kind"], ArtefactKind> = {
  screening_criteria: "screening_criteria",
  interview_guide: "interview_guide",
  assessment_matrix: "assessment_matrix",
  candidate_scoring_matrix: "candidate_scoring_matrix",
};

function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function dimensionCopyContent(dim: CapabilityDimension): { html: string; text: string } {
  const htmlParts: string[] = [`<p><strong>${escapeHtml(dim.name)}</strong></p>`];
  const textLines: string[] = [dim.name];

  if (dim.description) {
    htmlParts.push(`<p>${escapeHtml(dim.description)}</p>`);
    textLines.push(dim.description);
  }
  if (dim.positiveIndicators) {
    htmlParts.push(`<p><strong>Good:</strong> ${escapeHtml(dim.positiveIndicators)}</p>`);
    textLines.push(`Good: ${dim.positiveIndicators}`);
  }
  if (dim.negativeIndicators) {
    htmlParts.push(`<p><strong>Not:</strong> ${escapeHtml(dim.negativeIndicators)}</p>`);
    textLines.push(`Not: ${dim.negativeIndicators}`);
  }
  if (dim.successCriteria) {
    htmlParts.push(`<p><strong>Success:</strong> ${escapeHtml(dim.successCriteria)}</p>`);
    textLines.push(`Success: ${dim.successCriteria}`);
  }
  if (dim.idealScore != null) {
    htmlParts.push(`<p><em>Ideal score: ${dim.idealScore}</em></p>`);
    textLines.push(`Ideal score: ${dim.idealScore}`);
  }

  return { html: htmlParts.join(""), text: textLines.join("\n") };
}

function allDimensionsCopyContent(dims: CapabilityDimension[]): { html: string; text: string } {
  const items = dims.map((d) => dimensionCopyContent(d));
  const html = `<h3>Capability Dimensions</h3>${items.map((i) => i.html).join("<br>")}`;
  const text = ["Capability Dimensions", "", ...items.map((i) => i.text)].join("\n\n").trim();
  return { html, text };
}

interface RoleBlueprintStageProps {
  campaignId: string;
  onViewDeliverables?: () => void;
}

export function RoleBlueprintStage({ campaignId, onViewDeliverables }: RoleBlueprintStageProps) {
  const [editingDimensions, setEditingDimensions] = useState(false);
  const [viewingVersion, setViewingVersion] = useState<BlueprintVersion | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [lockModalOpen, setLockModalOpen] = useState(false);
  const [unlockModalOpen, setUnlockModalOpen] = useState(false);
  const [relockModalOpen, setRelockModalOpen] = useState(false);
  const [cascadeModalOpen, setCascadeModalOpen] = useState(false);
  const [cascadeDefaultKinds, setCascadeDefaultKinds] = useState<ArtefactKind[] | undefined>(
    undefined,
  );

  const [{ data, fetching }, reexecute] = useQuery({
    query: ROLE_BLUEPRINT_QUERY,
    variables: { id: campaignId },
  });
  const [{ fetching: savingDimensions }, updateDimensions] = useMutation(
    UPDATE_CAPABILITY_DIMENSIONS,
  );
  const [{ fetching: toggling }, toggleLock] = useMutation(TOGGLE_ROLE_BLUEPRINT_LOCK);
  const [, relockBlueprint] = useMutation(RELOCK_ROLE_BLUEPRINT);
  const [relockErrors, setRelockErrors] = useState<string[]>([]);
  const [{ fetching: togglingApproval }, toggleApproval] = useMutation(
    TOGGLE_ROLE_BLUEPRINT_APPROVAL,
  );
  const [{ fetching: regenerating }, regenerateDimensions] = useMutation(
    REGENERATE_ROLE_BLUEPRINT_DIMENSIONS,
  );
  const [{ fetching: startingRun }, startRegenerateRun] = useMutation(START_REGENERATE_RUN);
  const [regenerateError, setRegenerateError] = useState<string | null>(null);
  const [regenerateConfirm, setRegenerateConfirm] = useState<{
    kind: "unlock_and_relock" | "manual_edits";
    warning: string;
  } | null>(null);
  const [runAllError, setRunAllError] = useState<string | null>(null);
  const [runAllManualEditsConfirm, setRunAllManualEditsConfirm] = useState<{
    kinds: string[];
  } | null>(null);

  const refresh = useCallback(() => reexecute({ requestPolicy: "network-only" }), [reexecute]);
  useCampaignChannel(campaignId, refresh);

  const blueprint = data?.campaign?.roleBlueprint;
  const screeningCriteria: ScreeningCriteria | null = data?.campaign?.screeningCriteria ?? null;
  const currentVersion: BlueprintVersion | null = blueprint?.currentVersion ?? null;
  const lastLockedVersion: BlueprintVersion | null = blueprint?.lastLockedVersion ?? null;
  const versions: BlueprintVersion[] = blueprint?.versions ?? [];
  const locked: boolean = blueprint?.locked ?? false;
  const lockedByName: string | null = blueprint?.lockedBy?.name ?? null;
  const clientApproved: boolean = blueprint?.clientApproved ?? false;
  const clientApprovedBy: string | null = blueprint?.clientApprovedBy ?? null;
  const lockable: boolean = blueprint?.lockable ?? false;
  const hasUncommittedEdits: boolean = blueprint?.hasUncommittedEdits ?? false;
  const uncoveredBriefFields: string[] = blueprint?.uncoveredBriefFields ?? [];
  const lockPreconditions: LockPrecondition[] = blueprint?.lockPreconditions ?? [];
  const lockEvents: LockEvent[] = blueprint?.lockEvents ?? [];
  const outOfSyncArtefacts: OutOfSyncArtefact[] = data?.campaign?.outOfSyncArtefacts ?? [];
  const linkedBlueprintArtefacts: LinkedBlueprintArtefact[] =
    data?.campaign?.linkedBlueprintArtefacts ?? [];
  const hasPriorLock = lockEvents.length > 0;
  const liveDimensions: CapabilityDimension[] = blueprint?.capabilityDimensions ?? [];
  const liveThresholdRule = blueprint?.hireThresholdRule ?? null;
  const unmetLabels = lockPreconditions.filter((p) => !p.met).map((p) => p.label);
  const lockTooltip = locked
    ? "Unlock blueprint"
    : lockable
      ? "Lock blueprint"
      : `Cannot lock — missing: ${unmetLabels.join(", ")}`;

  const handleConfirmLock = async () => {
    const wasLocked = locked;
    const result = await toggleLock({ input: { campaignId } });
    setLockModalOpen(false);
    reexecute({ requestPolicy: "network-only" });
    const payload = result?.data?.toggleRoleBlueprintLock;
    const errors = payload?.errors ?? [];
    const nowLocked = payload?.roleBlueprint?.locked ?? false;
    if (!wasLocked && nowLocked && errors.length === 0) {
      setCascadeDefaultKinds(undefined);
      setCascadeModalOpen(true);
    }
  };

  const handleConfirmUnlock = async (reason: string) => {
    const result = await toggleLock({ input: { campaignId, reason } });
    const errors = result?.data?.toggleRoleBlueprintLock?.errors ?? [];
    if (errors.length === 0) {
      setUnlockModalOpen(false);
    }
    reexecute({ requestPolicy: "network-only" });
  };

  const handleConfirmRelock = async (reason: string) => {
    // Uses the dedicated relockRoleBlueprint mutation (always calls
    // blueprint.lock!) so the flow is identical whether triggered from the
    // Lock button (unlocked → relock) or from the uncommitted-edits banner
    // (locked → relock). toggleRoleBlueprintLock would have unlocked the
    // banner-locked case instead of relocking it.
    const result = await relockBlueprint({ input: { campaignId, reason } });
    const payload = result?.data?.relockRoleBlueprint;
    const errors = payload?.errors ?? [];
    const nowLocked = payload?.roleBlueprint?.locked ?? false;
    setRelockErrors(errors);
    if (errors.length === 0) {
      setRelockModalOpen(false);
    }
    reexecute({ requestPolicy: "network-only" });
    if (nowLocked && errors.length === 0) {
      const defaults = outOfSyncArtefacts
        .map((a) => ARTEFACT_KIND_TO_CASCADE_KIND[a.kind])
        .filter((k): k is ArtefactKind => Boolean(k));
      setCascadeDefaultKinds(defaults.length > 0 ? defaults : undefined);
      setCascadeModalOpen(true);
    }
  };

  const closeRelockModal = () => {
    setRelockModalOpen(false);
    setRelockErrors([]);
  };

  const handleLockClick = () => {
    if (locked) {
      setUnlockModalOpen(true);
    } else if (lockable) {
      if (hasPriorLock) {
        setRelockErrors([]);
        setRelockModalOpen(true);
      } else {
        setLockModalOpen(true);
      }
    }
  };

  const handleRegenerateOne = (kind: OutOfSyncArtefact["kind"]) => {
    const cascadeKind = ARTEFACT_KIND_TO_CASCADE_KIND[kind];
    if (!cascadeKind) return;
    setCascadeDefaultKinds([cascadeKind]);
    setCascadeModalOpen(true);
  };

  const runRegenerateAll = async (confirmOverwrite: boolean) => {
    setRunAllError(null);
    const kinds = Array.from(new Set(outOfSyncArtefacts.map((a) => a.kind as string)));
    if (kinds.length === 0) return;
    const res = await startRegenerateRun({
      input: { campaignId, kinds, confirmOverwrite },
    });
    const payload = res.data?.startRegenerateRun;
    const errs = payload?.errors ?? [];
    if (errs.length) {
      setRunAllError(errs.join(", "));
      return;
    }
    if (payload?.status === "manual_edits_present" && !confirmOverwrite) {
      setRunAllManualEditsConfirm({ kinds: payload.skippedManualEdits ?? [] });
      return;
    }
    if (payload?.status === "already_running") {
      setRunAllError(
        "A regenerate run is already in flight — see the background-tasks indicator at the top.",
      );
      return;
    }
    if (payload?.status === "nothing_to_do") {
      // Out-of-sync indicator may be stale; refresh and let the panel update.
      reexecute({ requestPolicy: "network-only" });
      return;
    }
    setRunAllManualEditsConfirm(null);
    reexecute({ requestPolicy: "network-only" });
  };

  const handleRegenerateAllClick = () => runRegenerateAll(false);

  const runRegenerateDimensions = async (confirmOverwrite: boolean, autoRelock: boolean) => {
    setRegenerateError(null);
    const res = await regenerateDimensions({
      input: { campaignId, confirmOverwrite, autoRelock },
    });
    const payload = res.data?.regenerateRoleBlueprintDimensions;
    const errs = payload?.errors ?? [];
    if (errs.length) {
      setRegenerateError(errs.join(", "));
      return;
    }
    if (payload?.status === "manual_edits_present" && !confirmOverwrite) {
      setRegenerateConfirm({
        kind: "manual_edits",
        warning: payload.warning ?? "Manual edits to capability dimensions will be replaced.",
      });
      return;
    }
    setRegenerateConfirm(null);
    reexecute({ requestPolicy: "network-only" });
  };

  // Entry point for the banner button. When the blueprint is locked, we
  // confirm the unlock/relock cycle first; otherwise we go straight to the
  // mutation (which may still surface the manual-edits gate).
  const handleRegenerateClick = () => {
    if (locked) {
      setRegenerateConfirm({
        kind: "unlock_and_relock",
        warning:
          "This will unlock the blueprint, regenerate the capability dimensions from the current brief, and re-lock the blueprint once complete. Downstream artefacts will pin to the new locked version.",
      });
      return;
    }
    runRegenerateDimensions(false, false);
  };

  const handleRegenerateConfirm = () => {
    if (!regenerateConfirm) return;
    if (regenerateConfirm.kind === "unlock_and_relock") {
      // Close the unlock confirm and let the next response drive what shows
      // next — manual edits will reopen the dialog with a different message,
      // and "ok" closes it for good.
      setRegenerateConfirm(null);
      runRegenerateDimensions(false, true);
    } else {
      runRegenerateDimensions(true, locked);
    }
  };

  const handleToggleApproval = async () => {
    if (clientApproved) {
      await toggleApproval({ input: { campaignId } });
    } else {
      const name = window.prompt("Client approver name:");
      if (name === null) return;
      await toggleApproval({ input: { campaignId, approverName: name } });
    }
    reexecute({ requestPolicy: "network-only" });
  };

  const handleSaveDimensions = async (
    dimensions: {
      name: string;
      description: string;
      idealScore: number | null;
      positiveIndicators: string;
      negativeIndicators: string;
      successCriteria: string;
      continuityId?: string;
    }[],
  ) => {
    if (!blueprint) return;
    await updateDimensions({
      input: { roleBlueprintId: blueprint.id, dimensions },
    });
    setEditingDimensions(false);
    reexecute({ requestPolicy: "network-only" });
  };

  const dimensionsForDisplay: CapabilityDimension[] = viewingVersion
    ? (viewingVersion.capabilityDimensions ?? [])
    : (blueprint?.capabilityDimensions ?? []);

  if (fetching && !blueprint) {
    return <p className="text-sm text-text-muted">Loading blueprint...</p>;
  }

  return (
    <div className="space-y-6">
      <StalenessBanner
        campaignId={campaignId}
        kinds={[
          "role_blueprint",
          "screening_criteria",
          "interview_guide",
          "assessment_guide",
          "candidate_scoring_matrix",
          "assessment_matrix",
        ]}
      />

      {/* Uncovered-brief-fields banner — pinned to the top of the stage so a
          newly-uncovered field is the first thing the user sees. At least one
          structured brief field is populated but no capability dimension
          declares it as covered, so candidate assessments will not score
          against it explicitly. */}
      {currentVersion && uncoveredBriefFields.length > 0 && (
        <div
          data-testid="uncovered-brief-fields-banner"
          className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-100"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="font-medium">Brief fields not represented in the rubric</p>
              <p className="mt-1 text-xs">
                These populated brief fields are not declared as covered by any capability
                dimension, so candidate assessments won't score against them explicitly:{" "}
                <span className="font-medium">
                  {uncoveredBriefFields.map(labelForBriefField).join(", ")}
                </span>
                . Edit the capability dimensions, or regenerate the blueprint, to bring them into
                the rubric.
              </p>
              {regenerateError && (
                <p className="mt-2 text-xs text-red-700 dark:text-red-300">{regenerateError}</p>
              )}
            </div>
            <button
              type="button"
              onClick={handleRegenerateClick}
              disabled={regenerating}
              title={
                locked
                  ? "Unlock blueprint, regenerate dimensions from the current brief, and re-lock"
                  : "Regenerate capability dimensions from the current brief"
              }
              data-testid="regenerate-blueprint-from-banner"
              className="shrink-0 rounded bg-amber-600 px-3 py-1 text-xs font-medium text-white hover:bg-amber-500 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-amber-700 dark:hover:bg-amber-600"
            >
              {regenerating ? "Regenerating…" : "Regenerate blueprint"}
            </button>
          </div>
        </div>
      )}
      {/* Blueprint metadata header — lock + approval + version history. After
          story 2002, narrative role-profile content lives on the brief; the
          blueprint itself is now structural metadata for capability
          dimensions and hire-threshold rules. */}
      {currentVersion ? (
        <div className="rounded-lg bg-card p-4 shadow-sm ring-1 ring-ring">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-medium text-text-secondary">Role Blueprint</h3>
              <BlueprintStateBadge locked={locked} hasPriorLock={hasPriorLock} />
              <span className="text-xs text-text-faint">v{currentVersion.versionNumber}</span>
              {locked && lockedByName && (
                <span className="text-xs text-text-faint">Locked by {lockedByName}</span>
              )}
              {clientApproved && (
                <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700 dark:bg-green-900/30 dark:text-green-400">
                  Client approved{clientApprovedBy ? ` by ${clientApprovedBy}` : ""}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {versions.length > 1 && (
                <button
                  type="button"
                  onClick={() => setShowHistory(!showHistory)}
                  className="rounded px-2 py-1 text-xs text-text-muted hover:bg-card-hover"
                >
                  {showHistory ? "Hide history" : `${versions.length} versions`}
                </button>
              )}
              <button
                type="button"
                onClick={handleToggleApproval}
                disabled={togglingApproval}
                title={clientApproved ? "Remove client approval" : "Record client approval"}
                className={`flex items-center gap-1 rounded px-2 py-1 text-xs hover:bg-card-hover ${
                  clientApproved ? "text-green-600 dark:text-green-400" : "text-text-muted"
                }`}
              >
                <CheckCircle size={12} />
                {clientApproved ? "Approved" : "Approve"}
              </button>
              <button
                type="button"
                onClick={handleLockClick}
                disabled={toggling || (!locked && !lockable)}
                title={lockTooltip}
                className={`flex items-center gap-1 rounded px-2 py-1 text-xs hover:bg-card-hover disabled:cursor-not-allowed disabled:opacity-50 ${
                  locked ? "text-amber-500" : "text-text-muted"
                }`}
              >
                {locked ? <Lock size={12} /> : <Unlock size={12} />}
                {locked ? "Locked" : "Lock"}
              </button>
            </div>
          </div>
          {viewingVersion && (
            <p className="mt-2 text-xs text-text-faint">
              Viewing v{viewingVersion.versionNumber} — capability dimensions effective at that
              version are shown below.
            </p>
          )}
          {!locked && lockPreconditions.length > 0 && (
            <div className="mt-3 border-t border-border pt-3">
              <p className="mb-2 text-xs font-medium text-text-secondary">Lock preconditions</p>
              <LockPreconditionsList preconditions={lockPreconditions} />
            </div>
          )}
        </div>
      ) : (
        <div className="rounded-lg bg-card p-6 text-center shadow-sm ring-1 ring-ring">
          <FileText size={32} className="mx-auto text-text-faint" />
          <p className="mt-2 text-sm text-text-muted">No role blueprint yet</p>
        </div>
      )}

      {showHistory && (
        <BlueprintVersionHistory versions={versions} onViewVersion={setViewingVersion} />
      )}

      <BlueprintAuditPanel events={lockEvents} versions={versions} />

      <LinkedArtefactsPanel
        artefacts={linkedBlueprintArtefacts}
        campaignId={campaignId}
        onRegenerate={handleRegenerateOne}
        onRegenerateAll={handleRegenerateAllClick}
        regeneratingAll={startingRun}
      />
      {runAllError && (
        <p className="text-xs text-red-600 dark:text-red-400" data-testid="regenerate-all-error">
          {runAllError}
        </p>
      )}

      <LockBlueprintModal
        open={lockModalOpen}
        currentVersionNumber={currentVersion?.versionNumber ?? null}
        onClose={() => setLockModalOpen(false)}
        onConfirm={handleConfirmLock}
      />

      <UnlockBlueprintModal
        open={unlockModalOpen}
        onClose={() => setUnlockModalOpen(false)}
        onConfirm={handleConfirmUnlock}
      />

      <RelockBlueprintModal
        open={relockModalOpen}
        lastLockedVersion={lastLockedVersion}
        currentDimensions={liveDimensions}
        lastLockedThresholdRule={null}
        currentThresholdRule={
          liveThresholdRule
            ? {
                minScoreForCriticalDimensions:
                  liveThresholdRule.minScoreForCriticalDimensions ?? null,
                minOverallScore: liveThresholdRule.minOverallScore ?? null,
                minStrengthsCount: liveThresholdRule.minStrengthsCount ?? null,
              }
            : null
        }
        outOfSyncArtefacts={outOfSyncArtefacts}
        errors={relockErrors}
        onClose={closeRelockModal}
        onConfirm={handleConfirmRelock}
      />

      <CascadeGenerationModal
        open={cascadeModalOpen}
        campaignId={campaignId}
        defaultSelectedKinds={cascadeDefaultKinds}
        onClose={() => setCascadeModalOpen(false)}
        onViewDeliverables={
          onViewDeliverables
            ? () => {
                setCascadeModalOpen(false);
                onViewDeliverables();
              }
            : undefined
        }
      />

      <RegenerateConfirmDialog
        open={regenerateConfirm !== null}
        title={
          regenerateConfirm?.kind === "unlock_and_relock"
            ? "Unlock, regenerate, and re-lock"
            : "Manual edits will be replaced"
        }
        confirmLabel={
          regenerateConfirm?.kind === "unlock_and_relock"
            ? "Unlock and regenerate"
            : "Regenerate anyway"
        }
        warning={regenerateConfirm?.warning ?? ""}
        onCancel={() => setRegenerateConfirm(null)}
        onConfirm={handleRegenerateConfirm}
      />

      <RegenerateConfirmDialog
        open={runAllManualEditsConfirm !== null}
        title="Manual edits will be replaced"
        confirmLabel="Regenerate all anyway"
        warning={
          runAllManualEditsConfirm
            ? `Some of the out-of-sync artefacts have manual edits${
                runAllManualEditsConfirm.kinds.length > 0
                  ? ` (${runAllManualEditsConfirm.kinds.join(", ")})`
                  : ""
              }. Their current versions stay in history but a new system-generated version becomes current.`
            : ""
        }
        onCancel={() => setRunAllManualEditsConfirm(null)}
        onConfirm={() => {
          setRunAllManualEditsConfirm(null);
          runRegenerateAll(true);
        }}
      />

      {/* Uncommitted-edits banner — dim edits slipped through on a locked
          blueprint before lock guards were in place, or were made on an
          earlier campaign. A relock captures them and cascades to dependent
          artefacts. */}
      {hasUncommittedEdits && (
        <div
          data-testid="uncommitted-edits-banner"
          className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-100"
        >
          <div className="flex items-start justify-between gap-3">
            <p className="flex-1">
              Capability dimensions have been edited since the last lock. Relock the blueprint to
              capture these changes and update dependent artefacts.
            </p>
            <button
              type="button"
              onClick={() => {
                setRelockErrors([]);
                setRelockModalOpen(true);
              }}
              className="shrink-0 rounded bg-amber-600 px-3 py-1 text-xs font-medium text-white hover:bg-amber-500 dark:bg-amber-700 dark:hover:bg-amber-600"
            >
              Relock
            </button>
          </div>
        </div>
      )}

      {/* Capability Dimensions */}
      {blueprint &&
        (editingDimensions ? (
          <CapabilityDimensionsEditor
            initial={dimensionsForDisplay.map((d) => ({
              name: d.name,
              description: d.description ?? "",
              idealScore: d.idealScore,
              positiveIndicators: d.positiveIndicators ?? "",
              negativeIndicators: d.negativeIndicators ?? "",
              successCriteria: d.successCriteria ?? "",
              continuityId: d.continuityId,
            }))}
            onSave={handleSaveDimensions}
            onCancel={() => setEditingDimensions(false)}
            saving={savingDimensions}
          />
        ) : dimensionsForDisplay.length > 0 ? (
          <div className="rounded-lg bg-card p-4 shadow-sm ring-1 ring-ring">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-sm font-medium text-text-secondary">Capability Dimensions</h3>
              <div className="flex items-center gap-2">
                <CopyButton
                  ariaLabel="Copy all capability dimensions"
                  withLabel
                  size={12}
                  getContent={() => allDimensionsCopyContent(dimensionsForDisplay)}
                />
                <button
                  type="button"
                  onClick={() => setEditingDimensions(true)}
                  disabled={locked}
                  title={
                    locked
                      ? "Unlock blueprint to edit capability dimensions"
                      : "Edit capability dimensions"
                  }
                  className="flex items-center gap-1 rounded px-2 py-1 text-xs text-text-muted hover:bg-card-hover disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Pencil size={12} /> Edit
                </button>
              </div>
            </div>
            <div className="space-y-2">
              {dimensionsForDisplay.map((dim) => (
                <CapabilityDimensionRow key={dim.id} dim={dim} />
              ))}
            </div>
          </div>
        ) : (
          <div className="rounded-lg bg-card p-6 text-center shadow-sm ring-1 ring-ring">
            <p className="text-sm text-text-muted">No capability dimensions defined</p>
            <button
              type="button"
              onClick={() => setEditingDimensions(true)}
              disabled={locked}
              title={
                locked
                  ? "Unlock blueprint to add capability dimensions"
                  : "Add capability dimensions"
              }
              className="mt-3 rounded bg-accent px-3 py-1.5 text-sm font-medium text-white hover:bg-accent/90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Add Dimensions
            </button>
          </div>
        ))}

      {screeningCriteria && (
        <ScreeningCriteriaSection
          campaignId={campaignId}
          screeningCriteria={screeningCriteria}
          blueprintLocked={locked}
          onSaved={refresh}
        />
      )}
    </div>
  );
}

function CapabilityDimensionRow({ dim }: { dim: CapabilityDimension }) {
  const covered = dim.coveredBriefFields ?? [];
  return (
    <div className="flex items-center justify-between rounded border border-border px-3 py-2">
      <div className="flex-1">
        <span className="text-sm font-medium text-text-primary">{dim.name}</span>
        {covered.length > 0 && (
          <div className="mt-1 flex flex-wrap gap-1">
            {covered.map((field) => (
              <span
                key={field}
                title={`Scores against the brief's ${labelForBriefField(field)} field`}
                className="rounded-full bg-accent/10 px-2 py-0.5 text-[10px] font-medium text-accent"
              >
                {labelForBriefField(field)}
              </span>
            ))}
          </div>
        )}
        {dim.description && <p className="mt-1 text-xs text-text-muted">{dim.description}</p>}
        {dim.positiveIndicators && (
          <p className="mt-1 text-xs text-text-muted">
            <span className="font-medium text-emerald-700 dark:text-emerald-400">Good:</span>{" "}
            {dim.positiveIndicators}
          </p>
        )}
        {dim.negativeIndicators && (
          <p className="text-xs text-text-muted">
            <span className="font-medium text-rose-700 dark:text-rose-300">Not:</span>{" "}
            {dim.negativeIndicators}
          </p>
        )}
        {dim.successCriteria && (
          <p className="text-xs text-text-muted">
            <span className="font-medium text-text-secondary">Success:</span> {dim.successCriteria}
          </p>
        )}
      </div>
      {dim.idealScore != null && (
        <span className="ml-3 text-xs text-text-faint">Ideal {dim.idealScore}</span>
      )}
    </div>
  );
}
